import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import {MenuItem} from 'primeng/api';
import {Subject} from 'rxjs';
import { takeUntil, withLatestFrom} from 'rxjs/operators';
import {AuthorizeService} from 'src/api-authorization/authorize.service';
import { SessionSettings } from '../models/session.model';
import {SiteDataService} from '../services/site-data.service';
import {Environment} from '../models/environment.model';
import { NgTemplateOutlet } from '@angular/common';
import { Bind } from 'primeng/bind';
import { Menu } from 'primeng/menu';
import { LeftNavComponent } from '../left-nav/left-nav.component';
import { SafeUrlPipe } from '../../modules/pipes/pipe.safeUrl';
import { TradingPartnerSettings } from '../../models/content/navigation/navigation.model';
import { PillComponent, PillStatus } from '../../modules/components/pill/pill.component';

// Distinct colours so DEV and QA are distinguishable at a glance. The badge text carries the
// meaning, so colour is never load-bearing for assistive tech.
const ENVIRONMENT_PILL_STATUS: Partial<Record<Environment, PillStatus>> = {
    [Environment.DEV]: 'info',
    [Environment.QA]: 'warning',
    [Environment.LOCAL]: 'secondary'
};

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [RouterLink, NgTemplateOutlet, Bind, Menu, LeftNavComponent, SafeUrlPipe, PillComponent]
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
    authorizeService = inject(AuthorizeService);
    private siteDataService = inject(SiteDataService);
    private router = inject(Router);
    private cdr = inject(ChangeDetectorRef);
    private renderer = inject(Renderer2);

    user: SessionSettings;
    navigationMenuItems: MenuItem[] = [];
    loading = true;
    loginURL: string = window.location.origin;
    publicURL: string = window.location.origin;
    tradingPartnerSettings: TradingPartnerSettings;
    IsAdmin: boolean;
    menuItems: MenuItem[] = [];
    showMobileMenu = false;
    @Input() isUserLoggedIn = false;
    @ViewChild('mobileMenu') menu: ElementRef;
    @ViewChild('mobileToggle') toggleButton: ElementRef;
	intercomEnabled: boolean = false;
    private unsubscribe$ = new Subject<void>();
    isAgentPortal: boolean;
    environmentID: Environment;
    Environment = Environment;
    isLanguageLoading: boolean = false;



    showClientContacts = false;

    get isLoginPage(): boolean {
        return this.router.url.startsWith('/account/login');
    }

    get userDisplayName(): string {
        const fullName = [this.user?.firstName, this.user?.lastName]
            .filter(Boolean)
            .join(' ')
            .trim();
        if (fullName) {
            return fullName;
        }
        if (this.user?.highestRole) {
            return this.user.highestRole.replace(/([A-Z])/g, ' $1').trim();
        }
        return 'User';
    }

    get userInitials(): string {
        const name = this.userDisplayName;
        const parts = name.split(' ').filter(p => p.length > 0);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    }

    get homePageURL(): string {
        return this.publicURL;
    }

    get theme() {
        return this.tradingPartnerSettings?.theme;
    }

    get clientTypeID(): number | undefined {
        return this.tradingPartnerSettings?.tradingPartner?.clientTypeID;
    }

    // One asset for every environment. The per-environment variants this method used to return
    // (ichrax-logo.png / ichrax-logo-dev.png) were never in the repo. The environment signal they
    // were carrying is now the badge below. Kept as a method because the template binds to it.
    getIchraxLogoForCurrentEnvironment(): string {
        return '/assets/img/ICHRAx-logo-color.png';
    }

    get showEnvironmentBadge(): boolean {
        return !!this.environmentID && this.environmentID !== Environment.PROD;
    }

    get environmentLabel(): string {
        return Environment[this.environmentID];
    }

    get environmentPillStatus(): PillStatus {
        return ENVIRONMENT_PILL_STATUS[this.environmentID] ?? 'warning';
    }

    constructor() {
        /**
         * Detecting when mouse is clicked outside of the toggle or the flyout:
         *  https://stackblitz.com/edit/angular-click-outside-detect
         */
        this.renderer.listen('window', 'click', (e: Event) => {
            if (
                e.target !== this.menu?.nativeElement &&
                e.target !== this.toggleButton?.nativeElement &&
                !this.menu?.nativeElement.contains(e.target)
            ) {
                this.showMobileMenu = false;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isUserLoggedIn']) {
            this.setNavigationMenuItems();
        }
    }

    ngOnInit(): void {
        this.siteDataService.select(c => {
            return {
                isAdmin: c.isAdmin,

                loggedInUser: c.loggedInUser,
                environmentConfig: c.environmentConfig,
            }
        }).pipe(
            takeUntil(this.unsubscribe$),
            withLatestFrom(this.authorizeService.getUser())
        )
            .subscribe(d => {
                const data = d[0];
                this.IsAdmin = data.isAdmin;
                this.user = data.loggedInUser;
                this.environmentID = data.environmentConfig?.environmentID;


                this.buildNav();

            });



        // close the mobile flyout menu when navigation starts
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd && this.showMobileMenu) {
                this.showMobileMenu = false;
            }
        });
    }

    private buildNav(): void {
        this.setNavigationMenuItems();
        // The template gates the whole .nav-brand block (logo + environment badge) on `!loading`.
        // Every branch of the old buildNav() cleared this flag; a refactor dropped the assignments
        // and left the guard, so the brand block never rendered. Site data has arrived by the time
        // buildNav runs, so this is the point where the header is done loading.
        this.loading = false;
        this.cdr.markForCheck();
    }

    setNavigationMenuItems(): void {
        this.navigationMenuItems = [];
        if (this.isUserLoggedIn) {
            this.menuItems = this.getLoggedInUserOptionsMenu();
        }
    }

    getLoggedInUserOptionsMenu(): MenuItem[] {
        return [
            {
                label: 'Account',
                icon: 'far fa-user',
                routerLink: ['/account/manage'],
            },
            {
                label: 'Log Out',
                icon: 'fa fa-sign-out',
                routerLink: ['/account/logout'],
                state: {local: true},
            },
        ];
    }

    setNavigationColors(bgColor: string): void {
        if (bgColor) {
            document.documentElement.style.setProperty('--nav-top-sub-bg', bgColor);
            const darkerColor = this.darkenHexColor(bgColor, 0.9);
            document.documentElement.style.setProperty('--nav-top-sub-bg-hover', darkerColor);
        }
    }

    setCTAColors(bgColor: string): void {
        if (bgColor) {
            document.documentElement.style.setProperty('--home-banner-cta', bgColor);
        }
    }
    // WIP testing THEME colors.
    // adding Primary and Secondary:
    setPrimaryColor(color: string): void {
        if (color) {
            document.documentElement.style.setProperty('--primary', color);
        }
    }
    setSecondaryColor(color: string): void {
        if (color) {
            document.documentElement.style.setProperty('--secondary-600', color);
            // document.documentElement.style.setProperty('--secondary', color);
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    darkenHexColor(hexColor, modifier): string {
        // make color array
        const colorArr = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
        // darken the colors by multiplying each component color by the modifier
        const tempColor = colorArr
            ? {
                r: parseInt(colorArr[1], 16) * modifier,
                g: parseInt(colorArr[2], 16) * modifier,
                b: parseInt(colorArr[3], 16) * modifier,
            }
            : null;
        // create rgb property
        const ICHRAXlor = 'rgb(' + tempColor.r + ',' + tempColor.g + ',' + tempColor.b + ')';
        return ICHRAXlor;
    }

    navigateToDashboard(): void {
        if (!this.IsAdmin) {
            //this.employeeDashboardStore.closeLibrary();
            //this.employeeDashboardStore.closeQuestions();
            //hack..above code isnt working, employee dashboard store instance isnt
            //being shared between header component and dashboard component,
            //dont know why. when clicking vendor logo in top left, force page refresh, and
            //in ngoninit of dashboard component reset employee dashboard store values
            //so questions/library is closed
            this.router.navigateByUrl('/', {skipLocationChange: true}).then(() =>
                this.router.navigate(['/dashboard']));
        }
    }


}
